import { useEffect, useReducer } from "react";
import EN, { EN_PATTERNS } from "./i18n.en";

// Переводы ключуются самим русским текстом, а не выдуманными идентификаторами.
// Причина практическая: строк больше тысячи, и любой словарь с ключами вида
// accounts.table.header пришлось бы держать в голове при каждой правке разметки.
// С русским оригиналом в качестве ключа место использования читается без словаря,
// а отсутствующий перевод не ломает экран — он просто остаётся русским.
const STORAGE_KEY = "umbrella_lang";
const SUPPORTED = ["ru", "en"];
const DEFAULT_LANG = "ru";

const readStoredLang = () => {
  try {
    const raw = String(window.localStorage.getItem(STORAGE_KEY) || "").trim();
    return SUPPORTED.includes(raw) ? raw : DEFAULT_LANG;
  } catch (error) {
    // Приватное окно или запрещённые куки — язык просто не запоминается.
    return DEFAULT_LANG;
  }
};

let currentLang = typeof window === "undefined" ? DEFAULT_LANG : readStoredLang();
const listeners = new Set();

export const getLang = () => currentLang;

export const setLang = (lang) => {
  const next = SUPPORTED.includes(lang) ? lang : DEFAULT_LANG;
  if (next === currentLang) return;
  currentLang = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, next);
  } catch (error) {
    // Не сохранилось — язык доживёт до перезагрузки страницы, и только.
  }
  listeners.forEach((notify) => notify());
};

// Подписка на смену языка. Компоненты не мемоизированы, поэтому достаточно
// перерисовать корень — дерево обновится следом.
export const useLang = () => {
  const [, force] = useReducer((n) => n + 1, 0);
  useEffect(() => {
    listeners.add(force);
    return () => listeners.delete(force);
  }, []);
  return currentLang;
};

const lookup = (key) => {
  const hit = EN[key];
  if (hit !== undefined) return hit;
  for (const [pattern, render] of EN_PATTERNS) {
    const match = pattern.exec(key);
    if (match) return render(match);
  }
  return undefined;
};

// Обычная строка.
export const t = (text) => {
  if (currentLang === "ru") return text;
  // Сообщения приходят и от сервера, где поле может быть пустым. Возвращаем как
  // есть, чтобы вызывающий код спокойно писал t(result?.error) || t("запасной").
  if (!text) return text;
  const key = String(text);
  const hit = lookup(key);
  if (hit !== undefined) return hit;
  // Ведущие и хвостовые пробелы в разметке значимы (` из `), но ключ словаря
  // хранится подрезанным — иначе одна и та же фраза дробилась бы на варианты.
  const trimmed = key.trim();
  if (trimmed !== key && lookup(trimmed) !== undefined) {
    return key.replace(trimmed, lookup(trimmed));
  }
  // Составная ошибка сервера: «что делали — что случилось», где вторая часть —
  // понятный текст технической ошибки, первая буква которого опущена.
  const dash = key.indexOf(" — ");
  if (dash > 0) {
    const head = key.slice(0, dash);
    const tail = key.slice(dash + 3);
    const tailHit = lookup(tail) ?? lookup(tail.charAt(0).toUpperCase() + tail.slice(1));
    if (tailHit !== undefined) {
      const headHit = lookup(head) ?? lookup(`${head}.`);
      return `${headHit !== undefined ? String(headHit).replace(/\.$/, "") : head} — ${tailHit}`;
    }
  }
  return text;
};

// Шаблонная строка, применяется как тег: tf`Найдено ${n} объявлений`.
// Ключом служит выкройка с местами подстановки — «Найдено {0} объявлений», —
// поэтому перевод может переставлять их местами, что английскому порядку слов
// нередко и требуется.
export const tf = (strings, ...values) => {
  const raw = Array.isArray(strings) ? strings : [String(strings)];
  if (currentLang === "ru") {
    return raw.reduce((acc, part, i) => acc + part + (i < values.length ? String(values[i] ?? "") : ""), "");
  }
  const pattern = raw.reduce((acc, part, i) => acc + part + (i < values.length ? `{${i}}` : ""), "");
  const translated = EN[pattern];
  if (translated === undefined) {
    return raw.reduce((acc, part, i) => acc + part + (i < values.length ? String(values[i] ?? "") : ""), "");
  }
  return translated.replace(/\{(\d+)\}/g, (match, index) => {
    const value = values[Number(index)];
    return value === undefined || value === null ? "" : String(value);
  });
};

// Локаль для Intl и toLocale*-методов. Даты и числа форматируются средствами
// платформы, а она про наш язык не знает — без этого «6 сентября» оставалось
// русским посреди английского экрана. en-GB, а не en-US: день впереди месяца,
// как в остальной панели и как принято в Германии.
export const locale = () => (currentLang === "en" ? "en-GB" : "ru-RU");

export const LANGUAGES = [
  { value: "ru", label: "Русский" },
  { value: "en", label: "English" }
];
